<template>
  <!-- مفيش Navbar هنا: صفحة لوجين مستقلة -->
  <div
      class="page-header align-items-start"
      :style="{'--login-bg': `url(${bgPath})` }"
  >
    <!-- overlay (قناع للتعتيم) - reduced opacity since background is already dark -->
    <span class="mask bg-gradient-dark opacity-2"></span>

    <div class="container my-auto">
      <div class="row justify-content-center">
        <div class="col-12 text-center pt-4 mb-3">
          <h3 class="page-title">Admin Login</h3>
        </div>

        <div class="col-xl-4 col-lg-5 col-md-7 col-10">
          <div class="card z-index-0 fadeIn3 fadeInBottom">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div class="bg-gradient-primary shadow-primary border-radius-lg py-3 pe-1">
                <h4 class="text-white font-weight-bolder text-center mt-2 mb-0">
                  Sign in
                </h4>
              </div>
            </div>

            <div class="card-body">
              <Form role="form" class="text-start" :validation-schema="schema" @submit="handleLogin">
                <div class="mb-3">
                  <material-input-field
                      id="email"
                      v-model:value="user.email"
                      type="text"
                      label="Email"
                      name="email"
                      variant="static"
                  />
                </div>

                <div class="mb-3">
                  <material-input-field
                      id="password"
                      v-model:value="user.password"
                      type="password"
                      label="Password"
                      name="password"
                      variant="static"
                  />
                </div>

                <div class="text-center">
                  <material-button class="my-4 mb-2" variant="gradient" color="primary" full-width>
                    <span>Sign in</span>
                  </material-button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialInputField from "@/components/MaterialInputField.vue";
import MaterialButton from "@/components/MaterialButton.vue";
import { Form } from "vee-validate";
import * as Yup from "yup";
import { mapMutations } from "vuex";
import showSwal from "@/mixins/showSwal";

export default {
  name: "Login",
  components: {
    MaterialInputField,
    MaterialButton,
    Form,
  },
  data() {
    return {
      bgPath: require("@/assets/img/market-standard-bg.jpg"),
      user: {
        email: "",
        password: null,
      },
      schema: Yup.object().shape({
        email: Yup.string().required("Email is a required input"),
        password: Yup.string().required("Password is a required input"),
      }),
    };
  },
  methods: {
    ...mapMutations(["toggleEveryDisplay", "toggleHideConfig"]),
    async handleLogin() {
      try {
        const response = await this.$store.dispatch("auth/login", this.user);
        
        // Log response for debugging
        console.log('Login response in component:', response);
        console.log('Response type:', response?.type);
        console.log('Response permissions:', response?.permissions);
        
        if (response) {
          // Check user type - must be admin or moderator
          if (response.type === "admin" || response.type === "moderator") {
            // Check if user has dashboard access, otherwise redirect to first available page
            // Handle both string and object permissions
            const hasDashboardAccess = response.permissions?.some(permission => {
              const permissionName = typeof permission === 'string' ? permission : permission?.name;
              return ['view stats', 'view-stats', 'view dashboard', 'view-dashboard'].includes(permissionName);
            });
            
            if (hasDashboardAccess) {
              this.$router.push({ name: "Dashboard" });
            } else {
              // Redirect to first available page based on permissions
              this.redirectToFirstAvailablePage(response.permissions);
            }
          } else {
            // User type is not admin or moderator
            console.error('User type is not authorized:', response.type);
            showSwal.methods.showSwal({
              type: "error",
              message: `You are not authorized to access this panel! User type: ${response.type || 'unknown'}. Only admin or moderator users can access the dashboard.`,
              width: 500,
            });
          }
        } else {
          // No response received
          console.error('Login returned no response');
          showSwal.methods.showSwal({
            type: "error",
            message: "Login failed. No response received from server. Please try again.",
            width: 500,
          });
        }
      } catch (error) {
        console.error('Login error:', error);
        const errorMessage = error.response?.data?.message || error.message || "An error occurred. Please try again.";
        showSwal.methods.showSwal({
          type: "error",
          message: errorMessage,
          width: 500,
        });
      }
    },

    redirectToFirstAvailablePage(permissions) {
      const permissionRoutes = [
        { permissions: ['view stats', 'view-stats'], route: 'Dashboard' },
        { permissions: ['view subjects', 'view-subjects', 'create subjects', 'create-subjects', 'edit subjects', 'edit-subjects'], route: 'Subjects' },
        { permissions: ['view courses', 'view-courses', 'create courses', 'create-courses', 'edit courses', 'edit-courses'], route: 'Courses' },
        { permissions: ['view teachers', 'view-teachers', 'create teachers', 'create-teachers', 'edit teachers', 'edit-teachers'], route: 'Teachers' },
        { permissions: ['view questions', 'view-questions', 'create questions', 'create-questions', 'edit questions', 'edit-questions', 'view question options', 'view-question-options', 'create question options', 'create-question-options', 'edit question options', 'edit-question-options', 'view answers', 'view-answers', 'create answers', 'create-answers', 'edit answers', 'edit-answers', 'view homework', 'view-homework', 'create homework', 'create-homework', 'edit homework', 'edit-homework'], route: 'SessionQuestionsList' },
        { permissions: ['view reservations', 'view-reservations', 'create reservations', 'create-reservations', 'edit reservations', 'edit-reservations'], route: 'ReservationsIndex' },
        { permissions: ['view countries', 'view-countries', 'create countries', 'create-countries', 'edit countries', 'edit-countries'], route: 'Countries' }
      ];

      // Handle both string and object permissions
      for (const item of permissionRoutes) {
        if (permissions?.some(permission => {
          const permissionName = typeof permission === 'string' ? permission : permission?.name;
          return item.permissions.includes(permissionName);
        })) {
          this.$router.push({ name: item.route });
          return;
        }
      }

      // Fallback to no-access page if no specific permissions found
      this.$router.push({ name: "NoAccess" });
    },
  },
};
</script>

<style scoped>
/* صفحة اللوجين: الخلفية منبثقة عن ::before علشان ماتأثرش على الباقي */
.page-header {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

/* pseudo element يغطي الـviewport بالكامل - لازم ثابت */
.page-header::before {
  content: "";
  position: fixed;        /* ثابت بالنسبة للنافذة، مش بالنسبة للأب */
  inset: 0;               /* top:0; right:0; bottom:0; left:0; */
  z-index: -2;            /* تحت القناع وتحت المحتوى */
  background-image: var(--login-bg);
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  will-change: transform;
  /* لو عايز تأثير ثابت مع scroll، خليه fixed (زي دلوقتي).
     لو عايز الصورة تتحرك مع الصفحة خليه absolute بدل fixed */
}

/* القناع الداكن فوق الصورة */
.mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1; /* فوق الصورة، تحت المحتوى */
  pointer-events: none;
}

/* content فوق القناع */
.page-header .container {
  position: relative;
  z-index: 1;
  padding-top: 4rem;
  padding-bottom: 4rem;
  max-width: 100%;
}

/* card فوق كل حاجة */
.card {
  border: none;
  border-radius: 1rem;
  box-shadow: 0 20px 27px rgba(0,0,0,0.05);
  background-color: #fff;
  z-index: 2;
}

/* تنسيقات بسيطة */
.card-body { padding: 1.5rem !important; }
.card-header { background: transparent; border: none; }
.bg-gradient-primary { border-radius: 0.75rem; padding: 1rem; }
.page-title { color: #ffffff; font-size: 1.75rem; font-weight: 600; margin-bottom: 1rem; text-shadow: 2px 2px 4px rgba(0,0,0,0.5); }

/* responsiveness */
@media (max-width: 991px) {
  .card-body { padding: 1.25rem !important; }
}
@media (max-width: 575px) {
  .card-body { padding: 1rem !important; }
  .page-title { font-size: 1.5rem; }
}
</style>
